import { useState, useEffect, useRef } from 'react';
import { TrendingUp, Lightbulb, Users, Code } from 'lucide-react';

import ChooseUsImage from "@/assets/images/about/why-choose-us.png";
import ConsultingImage from "@/assets/images/about/Great-Consulting-Services.webp";
import BusinessGrowthImage from "@/assets/images/about/business-growth.webp";
import DevelopersImage from "@/assets/images/about/expert-developer.webp";

const items = [
	{
		id: 0,
		icon: TrendingUp,
		title: "Business Growth",
		description: "Strategic solutions for scalable success",
		gradient: "from-purple-600 to-blue-600",
		shadowColor: "shadow-purple-500/30",
		borderHover: "hover:border-purple-400/30",
		accentColor: "purple",
		image: BusinessGrowthImage
	},
	{
		id: 1,
		icon: Lightbulb,
		title: "Digital Solutions",
		description: "Innovative tech for modern challenges",
		gradient: "from-blue-600 to-purple-600",
		shadowColor: "shadow-blue-500/30",
		borderHover: "hover:border-blue-400/30",
		accentColor: "blue",
		image: ChooseUsImage
	},
	{
		id: 2,
		icon: Users,
		title: "Great Consulting Services",
		description: "Expert guidance every step of the way",
		gradient: "from-purple-600 to-blue-600",
		shadowColor: "shadow-purple-500/30",
		borderHover: "hover:border-purple-400/30",
		accentColor: "purple",
		image: ConsultingImage
	},
	{
		id: 3,
		icon: Code,
		title: "Expert Developers",
		description: "Skilled team delivering quality code",
		gradient: "from-blue-600 to-purple-600",
		shadowColor: "shadow-blue-500/30",
		borderHover: "hover:border-blue-400/30",
		accentColor: "blue",
		image: DevelopersImage
	},
];

const AUTO_INTERVAL = 3000;

export default function WhyChooseUs() {
	const [activeIndex, setActiveIndex] = useState(0);
	const [hoveredIndex, setHoveredIndex] = useState(null);
	const [visibleIndex, setVisibleIndex] = useState(0);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const timerRef = useRef(null);

	const displayIndex = hoveredIndex !== null ? hoveredIndex : activeIndex;

	// Smooth image transition
	useEffect(() => {
		if (displayIndex === visibleIndex) return;
		setIsTransitioning(true);
		const t = setTimeout(() => {
			setVisibleIndex(displayIndex);
			setIsTransitioning(false);
		}, 200);
		return () => clearTimeout(t);
	}, [displayIndex]);

	// Auto cycle
	useEffect(() => {
		if (hoveredIndex !== null) {
			clearInterval(timerRef.current);
			return;
		}
		timerRef.current = setInterval(() => {
			setActiveIndex((prev) => (prev + 1) % items.length);
		}, AUTO_INTERVAL);
		return () => clearInterval(timerRef.current);
	}, [hoveredIndex]);

	const handleMouseEnter = (index) => {
		setHoveredIndex(index);
	};

	const handleMouseLeave = () => {
		setHoveredIndex(null);
	};

	const handleClick = (index) => {
		setActiveIndex(index);
	};

	const currentItem = items[displayIndex];
	const isActive = (index) => index === displayIndex;

	return (
		<section className="relative pt-16 pb-12 md:pt-24 md:pb-16 overflow-hidden">
			{/* Animated background blobs */}
			<div className="absolute inset-0 opacity-30">
				<div className="absolute top-40 left-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
				<div className="absolute bottom-40 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
			</div>

			<div className="container relative z-10">
				<div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

					{/* Left: Feature list */}
					<div className="lg:order-1 order-2">
						<div className="mb-8">
							<span className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm text-xs md:text-sm font-semibold text-gray-300 tracking-wider uppercase">
								Why Choose Us
							</span>
							<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
								Build Reliable{" "}
								<span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
									and Reasonable
								</span>
							</h2>
						</div>

						<div className="grid grid-cols-1 gap-4">
							{items.map((item, index) => {
								const Icon = item.icon;
								const active = isActive(index);

								return (
									<div
										key={item.id}
										onMouseEnter={() => handleMouseEnter(index)}
										onMouseLeave={handleMouseLeave}
										onClick={() => handleClick(index)}
										className={`
											group flex items-center gap-4 backdrop-blur-sm border p-5 rounded-xl 
											transition-all duration-300 cursor-pointer relative overflow-hidden
											${active
												? item.accentColor === 'purple'
													? 'bg-white/10 border-purple-400/50 shadow-lg shadow-purple-500/10'
													: 'bg-white/10 border-blue-400/50 shadow-lg shadow-blue-500/10'
												: `bg-white/5 border-white/10 ${item.borderHover} hover:bg-white/10`
											}
										`}
									>
										{/* Active left accent bar */}
										<div
											className={`
												absolute left-0 top-0 bottom-0 w-0.5 rounded-full transition-all duration-300
												bg-gradient-to-b ${item.gradient}
												${active ? 'opacity-100' : 'opacity-0'}
											`}
										/>

										{/* Auto-progress bar at bottom */}
										{active && hoveredIndex === null && (
											<div
												className="absolute bottom-0 left-0 h-0.5 rounded-full"
												style={{
													background: `linear-gradient(to right, ${item.accentColor === 'purple' ? '#a855f7, #60a5fa' : '#60a5fa, #a855f7'})`,
													animation: `progressBar ${AUTO_INTERVAL}ms linear forwards`,
												}}
											/>
										)}

										<div
											className={`
												flex-shrink-0 bg-gradient-to-r ${item.gradient} p-3.5 rounded-lg 
												transition-all duration-300
												${active ? `shadow-lg ${item.shadowColor} scale-110` : 'group-hover:shadow-lg group-hover:' + item.shadowColor}
											`}
										>
											<Icon size={24} className="text-white" />
										</div>

										<div className="flex-1">
											<h3 className={`text-lg font-semibold transition-colors duration-200 ${active ? 'text-white' : 'text-white/80'}`}>
												{item.title}
											</h3>
											<p className={`text-sm transition-colors duration-200 ${active ? 'text-gray-200' : 'text-gray-400'}`}>
												{item.description}
											</p>
										</div>

										{/* Active indicator dot */}
										<div className={`
											flex-shrink-0 w-2 h-2 rounded-full transition-all duration-300
											bg-gradient-to-r ${item.gradient}
											${active ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}
										`} />
									</div>
								);
							})}
						</div>
					</div>

					{/* Right: Image panel */}
					<div className="lg:order-2 order-1 relative group">
						{/* Glow behind image — color shifts with active item */}
						<div
							className={`
								absolute inset-0 rounded-2xl blur-xl opacity-20 transition-all duration-700
								bg-gradient-to-r
								${currentItem.accentColor === 'purple'
									? 'from-purple-500 to-blue-500'
									: 'from-blue-500 to-purple-500'
								}
							`}
						/>

						<div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-sm h-[380px] md:h-[560px]">
							{/* Render all images, fade in/out */}
							{items.map((item, index) => (
								<img
									key={item.id}
									src={item.image}
									alt={item.title}
									className={`
										absolute inset-0 w-full h-full object-cover
										transition-opacity duration-500
										${index === visibleIndex && !isTransitioning ? 'opacity-100' : 'opacity-0'}
									`}
								/>
							))}

							{/* Caption overlay */}
							<div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
								<div className="flex items-center gap-2">
									<div className={`w-2 h-2 rounded-full bg-gradient-to-r ${currentItem.gradient} animate-pulse`} />
									<span className="text-white text-sm font-medium tracking-wide transition-all duration-300">
										{currentItem.title}
									</span>
								</div>
							</div>
						</div>

						{/* Decorative corners */}
						<div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-purple-400/50 rounded-tr-2xl pointer-events-none"></div>
						<div className="absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-blue-400/50 rounded-bl-2xl pointer-events-none"></div>

						{/* Step indicators */}
						<div className="absolute -right-3 top-1/2 -translate-y-1/2 flex flex-col gap-2">
							{items.map((item, index) => (
								<button
									key={item.id}
									onClick={() => { setActiveIndex(index); setHoveredIndex(null); }}
									className={`
										w-1.5 rounded-full transition-all duration-300
										bg-gradient-to-b ${item.gradient}
										${isActive(index) ? 'h-6 opacity-100' : 'h-1.5 opacity-30 hover:opacity-60'}
									`}
								/>
							))}
						</div>
					</div>
				</div>
			</div>

			<style>{`
				@keyframes progressBar {
					from { width: 0%; }
					to { width: 100%; }
				}
			`}</style>
		</section>
	);
}